<script setup lang="tsx">
import { onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useUserJourney } from '~/composables/apostatudo/use-user-journey';
import { fetchHomeMenuHandler } from '~/handlers/fetch-home-menu.handler';
import type { HomeMenuItem } from '~/handlers/ports/api-fetch-home-menu.port';
import { useDailyCheckinStore } from '~/stores/daily-checkin';
import { isDailyCheckinDeepLink } from '~/utils/apostatudo/daily-checkin-deep-link';

const menuItems = useState<HomeMenuItem[]>('home-menu-items', () => []);
const menuFetchPromise = useState<Promise<HomeMenuItem[]> | null>(
  'home-menu-fetch-promise',
  () => null,
);

const { isDesktop } = useLayout();

const isNavigationButtonsVisible = computed<boolean>(() => {
  return isDesktop.value;
});

// Check-in Diário: o item só existe pra quem o core disse que participa
// (`is_eligible`). O sinal é POSITIVO — deslogado não some do menu por castigo,
// some porque o board precisa de `user_id` e sem ele não há resposta; e um
// "não sei" não pode virar convite pra uma promoção que o jogador não tem.
const dailyCheckinStore = useDailyCheckinStore();
const { isEligible: isCheckinEligible } = storeToRefs(dailyCheckinStore);

const { isAuthenticated } = storeToRefs(useAuthStore());

// Quem decide audiência agora é o core, item a item. Saiu daqui a heurística de
// URL que escondia o hub Clube Fidelidade fora do desktop (ele é acessado pelo
// badge da barra/deep-link no mobile): isso hoje é `visible_on_mobile: false`
// no item, no painel — e não uma regra de front sobre o texto do link.
const visibleMenuItems = computed<HomeMenuItem[]>(() =>
  menuItems.value.filter((item) => {
    // `logged_in_only` vem do core: item marcado assim não existe pra visitante.
    if (item.logged_in_only && !isAuthenticated.value) return false;

    const visibleHere = isDesktop.value
      ? item.visible_on_desktop
      : item.visible_on_mobile;

    if (!visibleHere) return false;

    // Elegibilidade é POR JOGADOR e vem do board do check-in, não daqui: este
    // payload é global e fica 5 min em cache compartilhado no handler, então
    // nenhuma prop dele consegue responder "esse jogador participa?".
    // `logged_in_only` só separa visitante de logado — logado inelegível ainda
    // veria o item. Por isso este if não é substituível pelas flags.
    if (isDailyCheckinDeepLink(item.link_url || '')) {
      return isCheckinEligible.value;
    }

    return true;
  }),
);

// Item do check-in no menu deste jogador. Enquanto o menu não chega (ou se ele
// vier sem o item), não há pergunta a fazer sobre elegibilidade.
const hasCheckinItem = computed(() =>
  menuItems.value.some((item) => isDailyCheckinDeepLink(item.link_url || '')),
);

// Elegibilidade vem do board do check-in, então só dá pra saber pedindo. O
// `silent` é o que faz disso um aquecimento: não pisca 'loading' nem grava erro
// de rede no estado (mesmo uso que o hub faz ao abrir), e o cache de 5 min do
// handler faz o clique no item aproveitar esta resposta em vez de pedir outra.
//
// Observa o `userId` e não o `isAuthenticated`: o perfil aterrissa depois da
// sessão, e sem id o load não tem o que pedir — com o watch no id, o pedido sai
// no frame em que ele chega (login no modal, sem recarregar a página, incluído).
const { userId } = useUserData();

watch(
  [userId, hasCheckinItem],
  ([id, hasItem]) => {
    // Só no cliente: o board vai com o Bearer da sessão do navegador, que no
    // servidor não existe (e o menu em si só é buscado no onMounted).
    if (!import.meta.client) return;

    if (id && hasItem) dailyCheckinStore.load({ silent: true });
  },
  { immediate: true },
);

const hoveredItems = ref<Record<number, boolean>>({});

const route = useRoute();

const ACTIVE_COLOR = '#FF6B00';

const SUPPORT_LINK = 'support';

const AUTH_REQUIRED_LINKS = ['/mini-games/6606', '/vip/levels'];

const CATEGORY_PREFIX = '/games/category/';

const { openLogin } = useAuth();
const { openCashbackMenu } = useCashbackMenu();
const { homeMenuItemClickEvent } = useUserJourney();

function isCashbackRoute(url: string): boolean {
  return /[?#]open=cashback|#cashback/.test(url || '');
}

const isItemActive = (linkUrl: string): boolean => {
  const [path = '', queryString] = linkUrl.split('?');

  // Handle /games/category/{slug} → /games/category/all?initialCategory={slug} redirect
  if (
    path.startsWith(CATEGORY_PREFIX) &&
    !path.endsWith('/all') &&
    route.path === CATEGORY_PREFIX + 'all'
  ) {
    const slug = path.slice(CATEGORY_PREFIX.length);

    return route.query.initialCategory === slug;
  }

  const pathMatch = route.path === path || route.path.startsWith(path + '/');

  if (!pathMatch || !queryString) return pathMatch;

  const linkParams = new URLSearchParams(queryString);

  for (const [key, value] of linkParams) {
    if (route.query[key] !== value) return false;
  }

  return true;
};

const isSupportItem = (linkUrl: string): boolean => {
  return linkUrl === SUPPORT_LINK;
};

const handleItemClick = (item: HomeMenuItem, index: number) => {
  if (import.meta.client) homeMenuItemClickEvent(item.id, index + 1);

  if (isSupportItem(item.link_url)) {
    const { openSupportChat } = useLiveSupport();

    openSupportChat();

    return;
  }

  // Cashback: abre o modal sem sair da rota atual
  if (isCashbackRoute(item.link_url)) {
    openCashbackMenu();

    return;
  }

  // Roletinha: abre o modal global sem navegar de rota. O id do link
  // (/mini-games/6606) vira o fallback pra roleta antiga do Smartico.
  const miniGamePath = item.link_url.split('?')[0] ?? '';

  if (miniGamePath.startsWith('/mini-games/')) {
    if (!isAuthenticated.value) {
      openLogin();

      return;
    }

    useRouletteStore().open({
      fallbackMiniGameId: Number(miniGamePath.slice('/mini-games/'.length)),
    });

    return;
  }

  if (AUTH_REQUIRED_LINKS.includes(item.link_url) && !isAuthenticated.value) {
    openLogin();

    return;
  }

  navigateTo(item.link_url);
};

onMounted(async () => {
  if (menuItems.value.length) return;

  if (!menuFetchPromise.value) {
    menuFetchPromise.value = fetchHomeMenuHandler();
  }

  menuItems.value = await menuFetchPromise.value;
});
</script>

<template>
  <div
    id="divMenuHighlight"
    class="select-none lg:-mt-6 xl:-mt-8 pl-2.5 lg:pl-0 lg:pb-5"
    draggable="false"
    style="min-height: 90px"
  >
    <WidgetsCarousel
      :key="visibleMenuItems.length"
      :display="{ spaceBetweenItems: [12, 5] }"
      :enable-navigation-buttons="isNavigationButtonsVisible"
      item-class="snap-start"
      :items="visibleMenuItems"
      slider-fade="both"
    >
      <template #item="{ item, index }">
        <div
          class="flex flex-col items-center cursor-pointer pr-2.5"
          @click="handleItemClick(item, index)"
        >
          <div class="relative">
            <div
              :class="[
                'relative w-[54px] h-[54px] lg:w-[64px] lg:h-[64px] rounded-full border border-solid overflow-hidden transition-opacity duration-150 active:opacity-70',
              ]"
              :style="{
                borderColor: isItemActive(item.link_url)
                  ? ACTIVE_COLOR
                  : item.image_border_color ||
                    (!isDesktop ? '#ffffff' : 'rgba(255,255,255,0.6)'),
                borderWidth: isItemActive(item.link_url)
                  ? '2px'
                  : isDesktop
                    ? '1.5px'
                    : '1px',
                background: item.image_bg_color || '#1111d6',
              }"
            >
              <div
                :id="`horizontal-menu-${index}`"
                class="w-[54px] h-[54px] lg:w-[64px] lg:h-[64px] flex items-center justify-center rounded-full"
                @mouseleave="hoveredItems[index] = false"
                @mouseover="hoveredItems[index] = true"
              >
                <img
                  v-if="item.image_url"
                  :alt="item.title"
                  class="w-full h-full object-cover select-none transition-transform duration-150 pointer-events-none"
                  draggable="false"
                  :src="`${item.image_url}/public`"
                  :style="{
                    transform: `scale(${hoveredItems[index] ? 1.05 : 1})`,
                  }"
                />
              </div>
            </div>

            <div
              v-if="item.label"
              :id="`horizontal-menu-label-${index}`"
              class="absolute w-[55px] lg:w-[64px] h-[12px] -bottom-0.5 left-1/2 text-[8px] text-center px-0 rounded-sm overflow-hidden min-h-[12px]"
              :style="{
                display: 'block',
                background: item.label_bg_color || '#ff3400',
                color: item.label_font_color || '#ffffff',
                transform: 'translateX(-50%) skew(-20deg)',
              }"
            >
              <span
                class="inline-block w-full font-plusJakartaSans font-semibold text-[8px] leading-[12px] tracking-wide text-center"
                style="transform: skew(20deg)"
              >
                <p
                  :id="`horizontal-menu-text-${index}`"
                  class="whitespace-normal"
                >
                  {{ item.label }}
                </p>
              </span>
            </div>
          </div>
          <div
            class="text-[11px] lg:text-[12px] tracking-normal mt-1.5 font-medium text-center"
            :style="{
              color: isItemActive(item.link_url) ? ACTIVE_COLOR : '#FFFFFF',
            }"
          >
            {{ item.title }}
          </div>
        </div>
      </template>
    </WidgetsCarousel>
  </div>
</template>
